import type { CloudRuntimeConnectionDescriptor } from "./cloudRuntimeCredentialProvider";

const DEFAULT_RENEWAL_SKEW_MS = 2 * 60_000;
const ACTION_RENEWAL_SKEW_MS = 30_000;
const TRANSIENT_RETRY_MS = 30_000;

export type RuntimeAuthorityRenewalState =
  | { readonly kind: "scheduled"; readonly expiresAt: string; readonly renewAt: string }
  | { readonly kind: "renewing"; readonly expiresAt: string }
  | { readonly kind: "ready"; readonly expiresAt: string; readonly renewedAt: string }
  | {
      readonly kind: "error";
      readonly code: "authority_auth_rejected" | "authority_renewal_failed";
      readonly retryAt: string | null;
    };

interface RuntimeAuthorityRenewalInput {
  readonly initialDescriptor: CloudRuntimeConnectionDescriptor;
  readonly renew: () => Promise<CloudRuntimeConnectionDescriptor>;
  readonly onState?: (state: RuntimeAuthorityRenewalState) => void;
  readonly now?: () => number;
  readonly renewalSkewMs?: number;
}

export interface RuntimeAuthorityRenewal {
  readonly ensureLive: () => Promise<void>;
  readonly renewNow: () => Promise<void>;
  readonly dispose: () => void;
}

function parsedExpiry(descriptor: CloudRuntimeConnectionDescriptor): number {
  const value = descriptor.expiresAt ? Date.parse(descriptor.expiresAt) : Number.NaN;
  if (!Number.isFinite(value)) {
    throw new Error("Cloud runtime authority descriptor has no valid expiry.");
  }
  return value;
}

function isTerminalAuthFailure(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const status = (error as { readonly status?: unknown }).status;
  return status === 401 || status === 403;
}

export function createCloudRuntimeAuthorityRenewal(
  input: RuntimeAuthorityRenewalInput,
): RuntimeAuthorityRenewal {
  const now = input.now ?? Date.now;
  const renewalSkewMs = input.renewalSkewMs ?? DEFAULT_RENEWAL_SKEW_MS;
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let renewalPromise: Promise<void> | null = null;
  let currentExpiresAtMs = parsedExpiry(input.initialDescriptor);

  const clearTimer = () => {
    if (timer === null) return;
    globalThis.clearTimeout(timer);
    timer = null;
  };

  const schedule = (descriptor: CloudRuntimeConnectionDescriptor) => {
    if (disposed) return;
    clearTimer();
    const expiresAtMs = parsedExpiry(descriptor);
    currentExpiresAtMs = expiresAtMs;
    const renewAtMs = Math.max(now(), expiresAtMs - renewalSkewMs);
    input.onState?.({
      kind: "scheduled",
      expiresAt: new Date(expiresAtMs).toISOString(),
      renewAt: new Date(renewAtMs).toISOString(),
    });
    timer = globalThis.setTimeout(
      () => {
        timer = null;
        void renewNow().catch(() => undefined);
      },
      Math.max(0, renewAtMs - now()),
    );
  };

  const retryTransientFailure = () => {
    if (disposed) return null;
    const retryAtMs = now() + TRANSIENT_RETRY_MS;
    if (retryAtMs >= currentExpiresAtMs) return null;
    timer = globalThis.setTimeout(() => {
      timer = null;
      void renewNow().catch(() => undefined);
    }, TRANSIENT_RETRY_MS);
    return new Date(retryAtMs).toISOString();
  };

  const renewNow = (): Promise<void> => {
    if (disposed) return Promise.resolve();
    if (renewalPromise) return renewalPromise;
    clearTimer();
    input.onState?.({
      kind: "renewing",
      expiresAt: new Date(currentExpiresAtMs).toISOString(),
    });
    const next = input
      .renew()
      .then(async (descriptor) => {
        const expiresAtMs = parsedExpiry(descriptor);
        if (expiresAtMs <= now()) {
          throw new Error("Cloud runtime authority descriptor is already expired.");
        }
        if (disposed) return;
        input.onState?.({
          kind: "ready",
          expiresAt: new Date(expiresAtMs).toISOString(),
          renewedAt: new Date(now()).toISOString(),
        });
        schedule(descriptor);
      })
      .catch((error: unknown) => {
        if (disposed) return;
        const terminal = isTerminalAuthFailure(error);
        input.onState?.({
          kind: "error",
          code: terminal ? "authority_auth_rejected" : "authority_renewal_failed",
          retryAt: terminal ? null : retryTransientFailure(),
        });
        throw error;
      })
      .finally(() => {
        renewalPromise = null;
      });
    renewalPromise = next;
    return next;
  };

  schedule(input.initialDescriptor);

  return {
    ensureLive: () =>
      currentExpiresAtMs > now() + ACTION_RENEWAL_SKEW_MS ? Promise.resolve() : renewNow(),
    renewNow,
    dispose: () => {
      disposed = true;
      clearTimer();
    },
  };
}
